/**
 * ModalScreen — formularios modales para crear items.
 * Modal kinds: 'create-incident', 'create-announcement'.
 *
 * Multi-step minimalista:
 *   1. Step navegación con D-pad (cambia entre campos o opciones)
 *   2. Step edición con teclado del sistema (input)
 *   3. Submit con A
 *
 * B para volver / cancelar.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import {
  announcementsApi,
  incidentsApi,
  ApiError,
} from '@/lib/api';
import { ArrowSprite } from '@/components/Sprites';

export function ModalScreen() {
  const { modal, closeModal, showToast } = useApp();
  const [step, setStep] = useState(0); // 0=title, 1=category/priority, 2=description, 3=submit

  // Incident state
  const [incTitle, setIncTitle] = useState('');
  const [incCategory, setIncCategory] = useState(0);
  const [incUrgency, setIncUrgency] = useState(0);
  const [incDescription, setIncDescription] = useState('');

  // Announcement state
  const [annTitle, setAnnTitle] = useState('');
  const [annPriority, setAnnPriority] = useState(1); // 0=normal, 1=important, 2=urgent
  const [annBody, setAnnBody] = useState('');

  const isIncident = modal?.kind === 'create-incident';
  const isAnnouncement = modal?.kind === 'create-announcement';

  const INC_CATEGORIES = ['ascensor', 'plomería', 'electricidad', 'estructura', 'limpieza', 'seguridad', 'jardín', 'otro'];
  const INC_URGENCIES = ['baja', 'media', 'alta'];
  const INC_URGENCY_API = ['low', 'medium', 'high'] as const;
  const ANN_PRIORITIES = ['normal', 'importante', 'urgente'];
  const ANN_PRIORITY_API = ['normal', 'important', 'urgent'] as const;

  const submit = useCallback(async () => {
    if (!modal) return;
    sfx.confirm();
    try {
      if (modal.kind === 'create-incident') {
        if (!incTitle.trim()) { showToast('Título requerido', 'error'); return; }
        const res = await incidentsApi.create({
          title: incTitle.trim(),
          description: incDescription.trim() || incTitle.trim(),
          category: INC_CATEGORIES[incCategory],
          urgency: INC_URGENCY_API[incUrgency],
        });
        if (res.success) { showToast('¡Reporte enviado!', 'success'); closeModal(); }
        else showToast(res.error || 'Error', 'error');
      } else if (modal.kind === 'create-announcement') {
        if (!annTitle.trim()) { showToast('Título requerido', 'error'); return; }
        const res = await announcementsApi.create({
          title: annTitle.trim(),
          content: annBody.trim() || annTitle.trim(),
          priority: ANN_PRIORITY_API[annPriority],
          send_push: annPriority >= 1,
        });
        if (res.success) { showToast('¡Anuncio publicado!', 'success'); closeModal(); }
        else showToast(res.error || 'Error', 'error');
      }
    } catch (err) {
      if (err instanceof ApiError) showToast(err.message, 'error');
      else showToast('Error de red', 'error');
    }
  }, [modal, incTitle, incDescription, incCategory, incUrgency, annTitle, annBody, annPriority, showToast, closeModal]);

  const stepCount = isIncident ? 4 : 3;

  const handleUpDown = useCallback((delta: number) => {
    sfx.tick();
    if (isIncident) {
      if (step === 1) {
        if (delta < 0) setIncCategory((c) => Math.max(0, c - 1));
        else setIncCategory((c) => Math.min(INC_CATEGORIES.length - 1, c + 1));
      } else if (step === 2) {
        if (delta < 0) setIncUrgency((u) => Math.max(0, u - 1));
        else setIncUrgency((u) => Math.min(INC_URGENCIES.length - 1, u + 1));
      }
    }
    if (isAnnouncement && step === 1) {
      if (delta < 0) setAnnPriority((p) => Math.max(0, p - 1));
      else setAnnPriority((p) => Math.min(ANN_PRIORITIES.length - 1, p + 1));
    }
  }, [step, isIncident, isAnnouncement]);

  // Hardware buttons
  useEffect(() => {
    const onUp = () => handleUpDown(-1);
    const onDown = () => handleUpDown(1);
    const onA = () => {
      if (step < stepCount - 1) {
        sfx.tick();
        setStep((s) => s + 1);
      } else {
        void submit();
      }
    };
    const onB = () => {
      sfx.cancel();
      if (step > 0) setStep((s) => s - 1);
      else closeModal();
    };
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, [handleUpDown, submit, step, stepCount, closeModal]);

  if (!modal) return null;

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <span className="gb-status-dot" />
          <span className="gb-pix gb-pix-xs">{isIncident ? 'NUEVO REPORTE' : 'NUEVO ANUNCIO'}</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5 }}>
            {step + 1}/{stepCount}
          </span>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '4px 4px', overflow: 'hidden' }}>
        <div className="gb-stack" style={{ gap: 5, height: '100%' }}>
          {isIncident && (
            <>
              {step === 0 && (
                <FormField
                  label="TÍTULO"
                  value={incTitle}
                  onChange={setIncTitle}
                  placeholder="¿Qué pasó?"
                />
              )}
              {step === 1 && (
                <SelectField
                  label="CATEGORÍA"
                  options={INC_CATEGORIES}
                  selected={incCategory}
                  onChange={setIncCategory}
                />
              )}
              {step === 2 && (
                <SelectField
                  label="URGENCIA"
                  options={INC_URGENCIES}
                  selected={incUrgency}
                  onChange={setIncUrgency}
                />
              )}
              {step === 3 && (
                <FormField
                  label="DESCRIPCIÓN"
                  value={incDescription}
                  onChange={setIncDescription}
                  placeholder="Detalles (opcional)"
                  multiline
                />
              )}
            </>
          )}
          {isAnnouncement && (
            <>
              {step === 0 && (
                <FormField
                  label="TÍTULO"
                  value={annTitle}
                  onChange={setAnnTitle}
                  placeholder="¿Qué anunciar?"
                />
              )}
              {step === 1 && (
                <SelectField
                  label="PRIORIDAD"
                  options={ANN_PRIORITIES}
                  selected={annPriority}
                  onChange={setAnnPriority}
                />
              )}
              {step === 2 && (
                <FormField
                  label="CUERPO"
                  value={annBody}
                  onChange={setAnnBody}
                  placeholder="Detalles (opcional)"
                  multiline
                />
              )}
            </>
          )}

          <div style={{ flex: 1 }} />

          <div className="gb-row" style={{ gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
            <span className="gb-pix gb-mid-text" style={{ fontSize: 5 }}>
              [{step < stepCount - 1 ? 'A SIGUIENTE' : 'A ENVIAR'} · B VOLVER]
            </span>
            {step === 0 && (
              <ArrowSprite size={8} dir="right" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  placeholder,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label className="gb-pix" style={{ fontSize: 6, color: 'var(--gb-mid)' }}>{label}</label>
      <div
        style={{
          borderTop: '1px solid var(--gb-dark)',
          borderBottom: '1px solid var(--gb-dark)',
          padding: '4px 2px',
        }}
      >
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="gb-pix"
            style={{
              fontSize: 8,
              width: '100%',
              minHeight: 60,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontFamily: 'inherit',
              color: 'var(--gb-dark)',
            }}
            maxLength={2000}
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="gb-pix"
            style={{
              fontSize: 8,
              width: '100%',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              color: 'var(--gb-dark)',
            }}
            maxLength={200}
          />
        )}
      </div>
    </div>
  );
}

function SelectField({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: number;
  onChange: (n: number) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label className="gb-pix" style={{ fontSize: 6, color: 'var(--gb-mid)' }}>
        {label} <span className="gb-mid-text" style={{ fontSize: 5 }}>[▲▼]</span>
      </label>
      <div
        style={{
          borderTop: '1px solid var(--gb-dark)',
          borderBottom: '1px solid var(--gb-dark)',
          padding: '4px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span className="gb-pix" style={{ fontSize: 9, color: 'var(--gb-dark)', textTransform: 'uppercase' }}>
          {options[selected]}
        </span>
        <ArrowSprite size={10} dir={selected > 0 ? 'left' : 'right'} />
      </div>
    </div>
  );
}